// src/screens/integrations/Integrations.js
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Facebook, Music2, ShoppingBag, CreditCard, BarChart4,
  RefreshCw, CheckCircle2, AlertCircle, ExternalLink, Search, Filter,
  Activity, Zap, Plus, X, Key, Loader2, User, Globe, Shield, Crown,
  MessageCircle, ArrowRight, Instagram,
} from 'lucide-react';
import './Integrations.css';



const API_BASE = 'https://revexia.ai/api';
const getToken = () => localStorage.getItem('access_token') || '';

// Only attach Authorization header when a token actually exists.
const authHeaders = () => {
  const token = getToken();
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const hasToken = () => Boolean(getToken());

const isDev = process.env.NODE_ENV !== 'production';
const dbg = (...args) => { if (isDev) console.log('[Integrations]', ...args); };



/* ── Coming Soon overlay ──────────────────────────────────────────────────── */
const ComingSoonCard = ({ children }) => (
  <div style={{ position: 'relative', height: '100%' }}>
    <div style={{
      filter: 'grayscale(0.4) brightness(0.65)',
      pointerEvents: 'none', userSelect: 'none', height: '100%',
    }}>
      {children}
    </div>
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(10,11,20,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 'inherit', zIndex: 10,
    }}>
      <span style={{
        fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: '#ffffff', background: '#808080',
        border: '1px solid #808080', borderRadius: '999px', padding: '0.35rem 0.9rem',
        boxShadow: '0 2px 12px rgba(239,68,68,0.4)',
      }}>Coming Soon</span>
    </div>
  </div>
);



/* ── Leave Toast ──────────────────────────────────────────────────────────── */
const LeaveToast = ({ onDismiss }) => (
  <>
    <style>{`
      @keyframes intToastUp {
        from { opacity: 0; transform: translateX(-50%) translateY(24px); }
        to   { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `}</style>
    <div style={{
      position: 'fixed', bottom: '2rem', left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, background: 'rgba(20,21,35,0.97)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '1rem', padding: '1.25rem 1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      backdropFilter: 'blur(16px)', minWidth: '340px', maxWidth: '440px', width: '90vw',
      animation: 'intToastUp 0.35s cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
            background: 'rgba(0,178,255,0.12)', border: '1px solid rgba(0,178,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MessageCircle size={18} style={{ color: '#00B2FF' }} />
          </div>
          <div style={{
            width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
            background: 'rgba(149,191,71,0.12)', border: '1px solid rgba(149,191,71,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ShoppingBag size={18} style={{ color: '#95BF47' }} />
          </div>
          <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', margin: 0, lineHeight: 1.4 }}>
            Connect your store and DMs to unlock AI signals
          </h3>
        </div>
        <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: '2px', flexShrink: 0 }}>
          <X size={16} />
        </button>
      </div>
      <p style={{ fontSize: '0.9rem', color: '#6B7280', margin: '0 0 1rem', lineHeight: 1.6 }}>
        Revexia reads your ManyChat conversations and Shopify visits to show you which customers are most likely to buy right now.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={onDismiss} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '0.4rem', padding: '0.65rem 1rem', background: '#3B82F6', borderRadius: '999px',
          fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#fff', border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(59,130,246,0.3)',
        }}>Set Up Integrations <ArrowRight size={13} /></button>
        <button onClick={onDismiss} style={{
          flex: 1, padding: '0.65rem 1rem', background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '999px',
          fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#9CA3AF', cursor: 'pointer',
        }}>Maybe Later</button>
      </div>
      <p style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#4B5563', letterSpacing: '0.04em', textAlign: 'center' }}>
        Follow the instructions sent via email
      </p>
    </div>
  </>
);



/* ── OAuth param extractor ────────────────────────────────────────────────── */
const extractOAuthParams = (popup) => {
  try {
    const url   = new URL(popup.location.href);
    const code  = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');
    dbg('extractOAuthParams → code:', code ? code.slice(0, 20) + '…' : null, '| state:', state, '| error:', error);
    return { code, state, error };
  } catch {
    return { code: null, state: null, error: null };
  }
};

/* ── Format last synced timestamp ─────────────────────────────────────────── */
const formatLastSynced = (value) => {
  if (!value || value === 'NEVER') return 'Never';
  if (value === 'JUST NOW') return 'Just now';
  // ISO timestamp
  try {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const diffMs  = Date.now() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1)  return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24)   return `${diffH}h ago`;
      return `${Math.floor(diffH / 24)}d ago`;
    }
  } catch {}
  return value;
};



/* ═══════════════════════════════════════════════════════════════════════════ */
const Integrations = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [visible,    setVisible]    = useState(false);
  const [showToast,  setShowToast]  = useState(false);

  // ── ManyChat state ────────────────────────────────────────────────────────
  const [showManyChatModal, setShowManyChatModal] = useState(false);
  const [apiKey,            setApiKey]            = useState('');
  const [isSaving,          setIsSaving]          = useState(false);
  const [saveError,         setSaveError]         = useState('');
  const [accountData,       setAccountData]       = useState(null);
  const [step,              setStep]              = useState('form');
  const [manyChatConnected, setManyChatConnected] = useState(false);
  const [manyChatAccount,   setManyChatAccount]   = useState(null);
  const [manyChatEvents,    setManyChatEvents]    = useState(0);
  const [manyChatLastSync,  setManyChatLastSync]  = useState('Never');

  // ── Facebook state ────────────────────────────────────────────────────────
  const [showFbModal,     setShowFbModal]     = useState(false);
  const [fbConnected,     setFbConnected]     = useState(false);
  const [fbConnecting,    setFbConnecting]    = useState(false);
  const [fbDisconnecting, setFbDisconnecting] = useState(false);
  const [fbError,         setFbError]         = useState('');
  const [fbAccount,       setFbAccount]       = useState(null);
  const [fbEvents,        setFbEvents]        = useState(0);
  const [fbLastSync,      setFbLastSync]      = useState('Never');
  const oauthPopupRef  = useRef(null);
  const fbPollTimerRef = useRef(null);
  const fbChannelRef   = useRef(null);

  // ── Instagram state ───────────────────────────────────────────────────────
  const [showIgModal,     setShowIgModal]     = useState(false);
  const [igConnected,     setIgConnected]     = useState(false);
  const [igConnecting,    setIgConnecting]    = useState(false);
  const [igDisconnecting, setIgDisconnecting] = useState(false);
  const [igError,         setIgError]         = useState('');
  const [igAccount,       setIgAccount]       = useState(null);
  const [igEvents,        setIgEvents]        = useState(0);
  const [igLastSync,      setIgLastSync]      = useState('Never');
  const igOauthPopupRef  = useRef(null);
  const igPollTimerRef   = useRef(null);
  const igChannelRef     = useRef(null);
  const igExchangingRef  = useRef(false);

  // ── Shopify state ─────────────────────────────────────────────────────────
  const [shopifyConnected,   setShopifyConnected]   = useState(false);
  const [shopifyEvents,      setShopifyEvents]      = useState(0);
  const [shopifyLastSync,    setShopifyLastSync]    = useState('Never');
  const [shopifyAccount,     setShopifyAccount]     = useState(null);
  const [showShopifyModal,   setShowShopifyModal]   = useState(false);
  const [shopifyStep,        setShopifyStep]        = useState('install');
  const [shopifyGenerating,  setShopifyGenerating]  = useState(false);
  const [shopifyError,       setShopifyError]       = useState('');
  const [shopifySnippet,     setShopifySnippet]     = useState('');
  const [shopifySiteId,      setShopifySiteId]      = useState('');
  const [shopifyCopied,      setShopifyCopied]      = useState(false);
  const [shopifyVerifying,   setShopifyVerifying]   = useState(false);
  const [shopifyDisconnecting, setShopifyDisconnecting] = useState(false);



  // ── Entrance animation ────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  // ── Leave toast ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (manyChatConnected) return;
    const t = setTimeout(() => setShowToast(true), 4000);
    return () => clearTimeout(t);
  }, [manyChatConnected]);

  useEffect(() => {
    if (manyChatConnected) return;
    const handler = () => setShowToast(true);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [manyChatConnected]);

  // ── On mount: load all existing statuses ─────────────────────────────────
  useEffect(() => {
    if (hasToken()) {
      checkManyChatStatus();
      checkFbStatus();
      checkIgStatus();
      checkShopifyStatus();
    } else {
      dbg('No token on mount — skipping status checks');
    }

    return () => {
      if (fbPollTimerRef.current) clearInterval(fbPollTimerRef.current);
      if (igPollTimerRef.current) clearInterval(igPollTimerRef.current);
      if (fbChannelRef.current) { try { fbChannelRef.current.close(); } catch {} }
      if (igChannelRef.current) { try { igChannelRef.current.close(); } catch {} }
    };
  }, []);



  /* ── Safe fetch wrapper ──────────────────────────────────────────────────
     Returns { ok, status, data } — never throws.
     401 / 403 / 404 treated as "not connected" silently.
  ─────────────────────────────────────────────────────────────────────────── */
  const safeFetch = async (url, options = {}) => {
    try {
      if (!hasToken()) {
        dbg('safeFetch skipped — no token:', url);
        return { ok: false, status: 0, data: null };
      }
      const res  = await fetch(url, { headers: authHeaders(), ...options });
      const data = await res.json().catch(() => null);
      dbg(`safeFetch ${res.status} ${url}`);
      return { ok: res.ok, status: res.status, data };
    } catch (err) {
      dbg('safeFetch network error:', url, err.message);
      return { ok: false, status: 0, data: null };
    }
  };



  // ── ManyChat status check (uses /manychat/status) ─────────────────────────
  // Response shape: { status: "CONNECTED"|"DISCONNECTED", events_tracked: N, last_synced: "..." }
  const checkManyChatStatus = async () => {
    const { ok, status, data } = await safeFetch(`${API_BASE}/manychat/status`);
    console.log('[API] GET /manychat/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      dbg('ManyChat connected — events:', data.events_tracked);
      setManyChatConnected(true);
      setManyChatEvents(data.events_tracked ?? 0);
      setManyChatLastSync(formatLastSynced(data.last_synced));
      // Also attempt to load the richer account details (name, avatar, etc.)
      fetchManyChatAccount();
    } else {
      dbg('ManyChat not connected — status:', status, '| data.status:', data?.status);
      setManyChatConnected(false);
      setManyChatAccount(null);
      setManyChatEvents(0);
      setManyChatLastSync('Never');
    }
  };

  // ── Fetch ManyChat account details (for modal display) ───────────────────
  const fetchManyChatAccount = async () => {
    const { ok, data } = await safeFetch(`${API_BASE}/manychat/account`);
    console.log('[API] GET /manychat/account →', { ok, body: data });
    if (ok && data?.data) {
      setManyChatAccount(data.data);
    }
  };



  // ── Facebook status check ─────────────────────────────────────────────────
  // Response shape: { status: "CONNECTED"|"DISCONNECTED", events_tracked: N, last_synced: "..." }
  const checkFbStatus = async () => {
    const { ok, status, data } = await safeFetch(`${API_BASE}/facebook/status`);
    console.log('[API] GET /facebook/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      const name = data?.page_name || data?.page || data?.name || null;
      dbg('FB connected — events:', data.events_tracked, '| page:', name);
      setFbConnected(true);
      setFbEvents(data.events_tracked ?? 0);
      setFbLastSync(formatLastSynced(data.last_synced));
      setFbAccount(name ? { page: name } : null);
      setFbError('');
      return true;
    } else {
      dbg('FB not connected — status:', status, '| data.status:', data?.status);
      setFbConnected(false);
      setFbAccount(null);
      setFbEvents(0);
      setFbLastSync('Never');
      return false;
    }
  };



  // ── Instagram status check ────────────────────────────────────────────────
  // Response shape: { status: "CONNECTED"|"DISCONNECTED", events_tracked: N, last_synced: "..." }
  const checkIgStatus = async () => {
    const { ok, status, data } = await safeFetch(`${API_BASE}/instagram/status`);
    console.log('[API] GET /instagram/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      const name = data?.username || data?.page || data?.name || null;
      dbg('IG connected — events:', data.events_tracked, '| handle:', name);
      setIgConnected(true);
      setIgEvents(data.events_tracked ?? 0);
      setIgLastSync(formatLastSynced(data.last_synced));
      setIgAccount(name ? { page: name } : null);
      setIgError('');
      return true;
    } else {
      dbg('IG not connected — status:', status, '| data.status:', data?.status);
      setIgConnected(false);
      setIgAccount(null);
      setIgEvents(0);
      setIgLastSync('Never');
      return false;
    }
  };



  // ── Shopify status check ──────────────────────────────────────────────────
  // Response shape: { status: "CONNECTED"|"DISCONNECTED", site_id, domain, events_tracked, last_synced, snippet }
  const checkShopifyStatus = async () => {
    const { ok, status, data } = await safeFetch(`${API_BASE}/shopify/status`);
    console.log('[API] GET /shopify/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      dbg('Shopify connected — events:', data.events_tracked, '| domain:', data.domain);
      setShopifyConnected(true);
      setShopifyEvents(data.events_tracked ?? 0);
      setShopifyLastSync(formatLastSynced(data.last_synced));
      setShopifyAccount({ domain: data.domain, siteId: data.site_id, snippet: data.snippet });
      if (data.snippet) setShopifySnippet(data.snippet);
      if (data.site_id) setShopifySiteId(data.site_id);
    } else {
      dbg('Shopify not connected — status:', status, '| data.status:', data?.status);
      setShopifyConnected(false);
      setShopifyAccount(null);
      setShopifyEvents(0);
      setShopifyLastSync('Never');
    }
  };

  // ── Shopify handlers ──────────────────────────────────────────────────────
  const handleOpenShopifyModal = () => {
    setShopifyError('');
    setShopifyCopied(false);
    if (shopifyConnected && shopifyAccount) {
      setShopifyStep('success');
      if (shopifyAccount.snippet) setShopifySnippet(shopifyAccount.snippet);
      if (shopifyAccount.siteId)  setShopifySiteId(shopifyAccount.siteId);
    } else {
      setShopifyStep('install');
      // Auto-generate a site ID when opening if we don't have one yet
      if (!shopifySiteId) handleShopifyGenerateId();
    }
    setShowShopifyModal(true);
  };

  const handleCloseShopifyModal = () => {
    setShowShopifyModal(false);
    setShopifyError('');
    setShopifyCopied(false);
    setShopifyVerifying(false);
  };

  const handleShopifyGenerateId = async () => {
    setShopifyGenerating(true);
    setShopifyError('');
    try {
      const { ok, status, data } = await safeFetch(`${API_BASE}/shopify/generate-id`, { method: 'POST' });
      console.log('[API] POST /shopify/generate-id →', { httpStatus: status, ok, body: data });
      if (!ok) throw new Error(data?.detail || `Server error ${status}`);
      const siteId  = data?.site_id  || data?.siteId  || data?.id  || '';
      const snippet = data?.snippet  || data?.code    || '';
      if (!siteId) throw new Error('No site ID returned from server.');
      setShopifySiteId(siteId);
      setShopifySnippet(snippet);
    } catch (err) {
      setShopifyError(err.message || 'Failed to generate site ID. Please try again.');
    } finally {
      setShopifyGenerating(false);
    }
  };

  const handleShopifyCopySnippet = () => {
    const text = shopifySnippet || buildSnippet(shopifySiteId);
    navigator.clipboard.writeText(text).then(() => {
      setShopifyCopied(true);
      setTimeout(() => setShopifyCopied(false), 2500);
    });
  };

  const buildSnippet = (siteId) =>
    `<!-- Revexia Configuration -->
<script>
  window.RevexiaTrackerConfig = {
    siteId: "${siteId}"
  };
</script>

<!-- Revexia Tracker via CDN -->
<script src="https://cdn.jsdelivr.net/gh/Software-Bari/Revexia-CDN@main/revexia-tracker.js" async></script>`;

  const handleShopifyVerify = async () => {
    setShopifyVerifying(true);
    setShopifyError('');
    try {
      await checkShopifyStatus();
      if (shopifyConnected) {
        setShopifyStep('success');
      } else {
        setShopifyError('Snippet not detected yet. Make sure it is pasted into your Shopify theme and saved.');
      }
    } catch {
      setShopifyError('Verification failed. Please try again.');
    } finally {
      setShopifyVerifying(false);
    }
  };

  const handleShopifyDisconnect = async () => {
    setShopifyDisconnecting(true);
    setShopifyError('');
    await safeFetch(`${API_BASE}/shopify/disconnect`, { method: 'POST' });
    setShopifyConnected(false);
    setShopifyAccount(null);
    setShopifyEvents(0);
    setShopifyLastSync('Never');
    setShopifySnippet('');
    setShopifySiteId('');
    setShopifyStep('install');
    setShopifyDisconnecting(false);
    setShowShopifyModal(false);
  };



  /* ── OAuth callback helpers ───────────────────────────────────────────────
     IMPORTANT: The Instagram /login endpoint returns a redirect_uri pointing to
     /facebook/callback (shared callback route) with state=instagram.
     So the IG token exchange must POST to /facebook/callback, not /instagram/callback.
  ─────────────────────────────────────────────────────────────────────────── */
  const callFbCallback = async (code, state = 'facebook') => {
    const url = `${API_BASE}/facebook/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    dbg('callFbCallback →', url.replace(code, code.slice(0, 20) + '…'));
    const { ok, status, data } = await safeFetch(url);
    if (ok) {
      const pageName = data?.page_name || data?.page || data?.name || data?.data?.page_name || data?.data?.name || null;
      return { success: true, pageName };
    }
    return { success: false, error: data?.detail || data?.message || `HTTP ${status}` };
  };

  // Instagram uses /facebook/callback with state=instagram (shared OAuth callback route)
  const callIgCallback = async (code, state = 'instagram') => {
    const url = `${API_BASE}/facebook/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    dbg('callIgCallback (via /facebook/callback) →', url.replace(code, code.slice(0, 20) + '…'));
    const { ok, status, data } = await safeFetch(url);
    if (ok) {
      const pageName = data?.username || data?.page || data?.name || data?.data?.username || data?.data?.name || null;
      return { success: true, pageName };
    }
    return { success: false, error: data?.detail || data?.message || `HTTP ${status}` };
  };



  // ── ManyChat handlers ─────────────────────────────────────────────────────
  const handleOpenModal = () => {
    if (manyChatConnected && manyChatAccount) { setAccountData(manyChatAccount); setStep('success'); }
    else setStep('form');
    setShowManyChatModal(true);
  };

  const handleCloseModal = () => {
    setShowManyChatModal(false);
    setApiKey('');
    setSaveError('');
    if (!manyChatConnected) { setAccountData(null); setStep('form'); }
  };

  const handleManyChatSubmit = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setIsSaving(true);
    setSaveError('');
    try {
      const postRes = await fetch(`${API_BASE}/manychat/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ api_key: apiKey }),
      });
      if (!postRes.ok) {
        const errData = await postRes.json().catch(() => ({}));
        throw new Error(errData?.detail || errData?.message || `Server error ${postRes.status}`);
      }
      // Re-check status to get events_tracked / last_synced
      await checkManyChatStatus();
      // Also try to get account detail for modal
      const { ok, data } = await safeFetch(`${API_BASE}/manychat/account`);
      if (!ok || !data?.data) throw new Error('Connected but could not load account details.');
      const acc = data.data;
      setAccountData(acc);
      setManyChatAccount(acc);
      setManyChatConnected(true);
      setStep('success');
      setShowToast(false);
    } catch (err) {
      setSaveError(err.message || 'Something went wrong. Check your API key.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnect = () => {
    setManyChatConnected(false);
    setManyChatAccount(null);
    setManyChatEvents(0);
    setManyChatLastSync('Never');
    setAccountData(null);
    setApiKey('');
    setSaveError('');
    setStep('form');
    setShowManyChatModal(false);
  };



  // ── Facebook OAuth handlers ───────────────────────────────────────────────
  const handleOpenFbModal  = () => { setFbError(''); setShowFbModal(true); };

  const handleCloseFbModal = () => {
    if (fbPollTimerRef.current) { clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null; }
    if (fbChannelRef.current)   { try { fbChannelRef.current.close(); } catch {} fbChannelRef.current = null; }
    if (oauthPopupRef.current && !oauthPopupRef.current.closed) oauthPopupRef.current.close();
    setFbConnecting(false);
    setFbError('');
    setShowFbModal(false);
  };

  const handleFbOAuth = async () => {
    setFbConnecting(true);
    setFbError('');
    if (fbPollTimerRef.current) clearInterval(fbPollTimerRef.current);
    if (fbChannelRef.current)   { try { fbChannelRef.current.close(); } catch {} }

    try {
      const { ok, status, data } = await safeFetch(`${API_BASE}/facebook/login`);
      dbg('FB /login status:', status);
      if (!ok) throw new Error(`Server returned ${status}. Please try again.`);
      const url = data?.url;
      if (!url) throw new Error('No OAuth URL returned from server.');

      const popup = window.open(url, 'fb_oauth_popup', 'width=600,height=700,scrollbars=yes,resizable=yes');
      oauthPopupRef.current = popup;
      if (!popup) throw new Error('Popup was blocked. Please allow popups for this site and try again.');
      dbg('FB popup opened');

      let handled = false;

      // Strategy A: BroadcastChannel
      try {
        const ch = new BroadcastChannel('oauth_channel');
        fbChannelRef.current = ch;
        ch.onmessage = async (event) => {
          dbg('BroadcastChannel FB message:', event.data);
          const platform = (event.data?.platform || '').toLowerCase();
          if (platform && platform !== 'facebook') return;

          handled = true;
          ch.close(); fbChannelRef.current = null;
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;

          const { type, code, state, pageName } = event.data;
          if (type === 'OAUTH_SUCCESS' || type === 'oauth_success' || type === 'success') {
            if (code) {
              const result = await callFbCallback(code, state || 'facebook');
              if (result.success) {
                await checkFbStatus();
              } else {
                await checkFbStatus();
              }
            } else {
              await checkFbStatus();
            }
            setFbConnecting(false);
            setShowFbModal(false);
          } else {
            setFbConnecting(false);
            setFbError(event.data?.error || 'Authorization failed. Please try again.');
          }
        };
      } catch (chErr) {
        dbg('BroadcastChannel not supported:', chErr.message);
      }

      // Strategy B: Poll popup URL
      fbPollTimerRef.current = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;
          if (handled) return;
          if (fbChannelRef.current) { try { fbChannelRef.current.close(); } catch {} fbChannelRef.current = null; }
          dbg('FB popup closed — re-checking status via API');
          await checkFbStatus();
          setFbConnecting(false);
          setShowFbModal(false);
          return;
        }
        if (handled) return;

        const { code, state, error } = extractOAuthParams(popup);
        if (error) {
          handled = true;
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;
          popup.close();
          setFbConnecting(false);
          setFbError(`Authorization denied: ${error}`);
          return;
        }
        if (code) {
          handled = true;
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;
          popup.close();
          const result = await callFbCallback(code, state || 'facebook');
          const isConn = await checkFbStatus();
          setFbConnecting(false);
          if (result.success || isConn) {
            setFbError('');
            setShowFbModal(false);
          } else {
            setFbError(result.error || 'Connection failed. Please try again.');
          }
        }
      }, 500);

    } catch (err) {
      dbg('handleFbOAuth error:', err.message);
      setFbError(err.message || 'Failed to start OAuth. Please try again.');
      setFbConnecting(false);
    }
  };

  const handleFbDisconnect = async () => {
    setFbDisconnecting(true);
    setFbError('');
    await safeFetch(`${API_BASE}/facebook/disconnect`, { method: 'POST' });
    setFbConnected(false);
    setFbAccount(null);
    setFbEvents(0);
    setFbLastSync('Never');
    setFbDisconnecting(false);
    setShowFbModal(false);
  };



  // ── Instagram OAuth handlers ──────────────────────────────────────────────
  const handleOpenIgModal  = () => { setIgError(''); setShowIgModal(true); };

  const handleCloseIgModal = () => {
    if (igPollTimerRef.current) { clearInterval(igPollTimerRef.current); igPollTimerRef.current = null; }
    if (igChannelRef.current)   { try { igChannelRef.current.close(); } catch {} igChannelRef.current = null; }
    if (igOauthPopupRef.current && !igOauthPopupRef.current.closed) igOauthPopupRef.current.close();
    igExchangingRef.current = false;
    setIgConnecting(false);
    setIgError('');
    setShowIgModal(false);
  };

  const handleIgOAuth = async () => {
    setIgConnecting(true);
    setIgError('');
    igExchangingRef.current = false;
    if (igPollTimerRef.current) clearInterval(igPollTimerRef.current);
    if (igChannelRef.current)   { try { igChannelRef.current.close(); } catch {} }

    try {
      const { ok, status, data } = await safeFetch(`${API_BASE}/instagram/login`);
      dbg('IG /login status:', status);
      if (!ok) throw new Error(`Server returned ${status}. Please try again.`);
      const url = data?.url;
      if (!url) throw new Error('No OAuth URL returned from server.');

      dbg('IG OAuth URL:', url);

      const popup = window.open(url, 'ig_oauth_popup', 'width=600,height=700,scrollbars=yes,resizable=yes');
      igOauthPopupRef.current = popup;
      if (!popup) throw new Error('Popup was blocked. Please allow popups for this site and try again.');
      dbg('IG popup opened');

      let handled = false;

      // Strategy A: BroadcastChannel — listens for message from callback page
      try {
        const ch = new BroadcastChannel('oauth_channel');
        igChannelRef.current = ch;
        ch.onmessage = async (event) => {
          dbg('BroadcastChannel IG message:', event.data);
          const platform = (event.data?.platform || '').toLowerCase();
          if (platform && platform !== 'instagram' && platform !== 'facebook') return;

          handled = true;
          ch.close(); igChannelRef.current = null;
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;

          const { type, code, state } = event.data;
          if (type === 'OAUTH_SUCCESS' || type === 'oauth_success' || type === 'success') {
            if (code) {
              igExchangingRef.current = true;
              await callIgCallback(code, state || 'instagram');
              igExchangingRef.current = false;
            }
            await checkIgStatus();
            setIgConnecting(false);
            setShowIgModal(false);
          } else {
            setIgConnecting(false);
            setIgError(event.data?.error || 'Authorization failed. Please try again.');
          }
        };
      } catch (chErr) {
        dbg('BroadcastChannel (IG) not supported:', chErr.message);
      }

      // Strategy B: Poll the popup URL
      igPollTimerRef.current = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;
          if (handled) return;
          if (igChannelRef.current) { try { igChannelRef.current.close(); } catch {} igChannelRef.current = null; }

          dbg('IG popup closed — verifying status via API...');
          igExchangingRef.current = true;
          await new Promise(resolve => setTimeout(resolve, 1500));
          const isConn = await checkIgStatus();
          igExchangingRef.current = false;

          setIgConnecting(false);
          if (!isConn) {
            setIgError('Could not confirm connection. Please try again.');
          } else {
            setIgError('');
            setShowIgModal(false);
          }
          return;
        }
        if (handled || igExchangingRef.current) return;

        const { code, state, error } = extractOAuthParams(popup);
        if (error) {
          handled = true;
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;
          popup.close();
          setIgConnecting(false);
          setIgError(`Authorization denied: ${error}`);
          return;
        }
        if (code) {
          handled = true;
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;
          popup.close();
          igExchangingRef.current = true;
          await callIgCallback(code, state || 'instagram');
          igExchangingRef.current = false;
          const isConn = await checkIgStatus();
          setIgConnecting(false);
          if (isConn) {
            setIgError('');
            setShowIgModal(false);
          } else {
            dbg('IG callIgCallback failed, re-checking status...');
            setIgError('Connection failed. Please try again.');
          }
        }
      }, 500);

    } catch (err) {
      dbg('handleIgOAuth error:', err.message);
      setIgError(err.message || 'Failed to start OAuth. Please try again.');
      setIgConnecting(false);
    }
  };

  const handleIgDisconnect = async () => {
    setIgDisconnecting(true);
    setIgError('');
    await safeFetch(`${API_BASE}/instagram/disconnect`, { method: 'POST' });
    setIgConnected(false);
    setIgAccount(null);
    setIgEvents(0);
    setIgLastSync('Never');
    setIgDisconnecting(false);
    setShowIgModal(false);
  };



  // ── Icon components ───────────────────────────────────────────────────────
  const ManyChatIcon = ({ size = 48 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="10" fill="#00B2FF" />
      <image
        href="https://abduzeedo.com/sites/default/files/styles/max_2600x2600/public/originals/hero_manychat.png"
        x="6" y="6" width="36" height="36"
      />
    </svg>
  );

  const InstagramIcon = ({ size = 28, style }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={style}>
      <defs>
        <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
          <stop offset="0%"  stopColor="#fdf497" />
          <stop offset="5%"  stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect width="48" height="48" rx="12" fill="url(#ig-grad)" />
      <rect x="13" y="13" width="22" height="22" rx="6" stroke="#fff" strokeWidth="2.5" fill="none" />
      <circle cx="24" cy="24" r="5.5" stroke="#fff" strokeWidth="2.5" fill="none" />
      <circle cx="33.5" cy="14.5" r="1.5" fill="#fff" />
    </svg>
  );



  // ── Platform config ───────────────────────────────────────────────────────
  const platforms = [
    {
      name: 'ManyChat',
      icon: ManyChatIcon,
      status: manyChatConnected ? 'connected' : 'disconnected',
      eventsCount: manyChatEvents,
      lastSync: manyChatConnected ? manyChatLastSync : 'Never',
      color: '#00B2FF', clickable: true, comingSoon: false,
      onCardClick: handleOpenModal,
    },
    {
      name: 'Facebook',
      icon: Facebook,
      status: fbConnected ? 'connected' : 'disconnected',
      eventsCount: fbEvents,
      lastSync: fbConnected ? fbLastSync : 'Never',
      color: '#1877F2', comingSoon: false, clickable: true,
      onCardClick: handleOpenFbModal,
    },
    {
      name: 'Instagram',
      icon: ({ size = 28, style }) => <InstagramIcon size={size} style={style} />,
      status: igConnected ? 'connected' : 'disconnected',
      eventsCount: igEvents,
      lastSync: igConnected ? igLastSync : 'Never',
      color: '#d6249f', comingSoon: false, clickable: true,
      onCardClick: handleOpenIgModal,
    },
    {
      name: 'Shopify',
      icon: ShoppingBag,
      status: shopifyConnected ? 'connected' : 'disconnected',
      eventsCount: shopifyEvents,
      lastSync: shopifyConnected ? shopifyLastSync : 'Never',
      color: '#95BF47', comingSoon: false, clickable: true,
      onCardClick: handleOpenShopifyModal,
    },
    {
      name: 'TikTok',
      icon: Music2,
      status: 'connected', eventsCount: 4, lastSync: '1h ago',
      color: '#00f2ea', comingSoon: true,
    },
    {
      name: 'Stripe',
      icon: CreditCard,
      status: 'syncing', eventsCount: 6, lastSync: 'Syncing...',
      color: '#635BFF', comingSoon: true,
    },
    {
      name: 'Google Analytics',
      icon: BarChart4,
      status: 'disconnected', eventsCount: 0, lastSync: 'Never',
      color: '#F4B400', comingSoon: true,
    },
  ];

  const filteredPlatforms = platforms.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statsSummary = [
    { icon: Activity,     label: 'Total Webhooks', value: '1,240 / hr', color: 'blue'    },
    { icon: Zap,          label: 'Active Syncs',   value: '6 Platforms', color: 'purple' },
    { icon: CheckCircle2, label: 'Data Health',    value: '99.8%',       color: 'emerald'},
  ];

  const renderCard = (platform) => (
    <div
      className={`integration-card ${platform.clickable ? 'integration-card-clickable' : ''}`}
      onClick={() => platform.clickable && !platform.comingSoon && platform.onCardClick?.()}
      style={{ height: '100%' }}
    >
      <div className="integration-card-glow" style={{ backgroundColor: platform.color }} />
      <div className="integration-card-header">
        <div className="integration-card-header-left">
          <div
            className="integration-platform-icon"
            style={{ backgroundColor: `${platform.color}20`, borderColor: `${platform.color}40` }}
          >
            <platform.icon size={28} style={{ color: platform.color }} />
          </div>
          <div className="integration-platform-info">
            <h3 className="integration-platform-name">{platform.name}</h3>
            <div className="integration-status-indicator">
              <span className={`status-dot status-${platform.status}`} />
              <span className={`status-text status-${platform.status}`}>{platform.status}</span>
            </div>
          </div>
        </div>
        <button className="integration-external-btn" onClick={(e) => e.stopPropagation()}>
          <ExternalLink size={18} />
        </button>
      </div>
      <div className="integration-card-body">
        <div className="integration-events-card">
          <div className="integration-events-header">
            <span className="integration-events-label">Events Tracked</span>
            <span className="integration-events-count">{platform.eventsCount}</span>
          </div>
          <div className="integration-events-progress-track">
            <div
              className="integration-events-progress-bar"
              style={{
                width: `${Math.min(platform.eventsCount * 8, 100)}%`,
                background: `linear-gradient(to right, ${platform.color}, ${platform.color}CC)`,
                boxShadow: `0 0 10px ${platform.color}50`,
              }}
            />
          </div>
        </div>
        <div className="integration-sync-info">
          <span className="sync-info-label">Last Synced</span>
          <span className="sync-info-value">
            <RefreshCw size={10} className={platform.status === 'syncing' ? 'sync-icon syncing' : 'sync-icon'} />
            {platform.lastSync}
          </span>
        </div>
      </div>
      <div className="integration-card-footer">
        <button
          className={`integration-action-btn ${platform.status === 'disconnected' ? 'primary' : 'secondary'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (platform.clickable && !platform.comingSoon) platform.onCardClick?.();
          }}
        >
          {platform.status === 'disconnected' ? 'Initialize Connection' : 'Manage Connection'}
        </button>
      </div>
    </div>
  );



  return (
    <>
      <Helmet>
        <title>Integrations - Revexia AI Revenue Brain</title>
        <meta name="description" content="Platform integrations and data sync management" />
      </Helmet>

      <style>{`
        @keyframes intPageEntrance {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes intToastUp {
          from { opacity: 0; transform: translateX(-50%) translateY(24px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes mcSpin { to { transform: rotate(360deg); } }
        .mc-spinner { animation: mcSpin 0.8s linear infinite; }
      `}</style>

      {showToast && !manyChatConnected && (
        <LeaveToast onDismiss={() => setShowToast(false)} />
      )}

      <div
        className="integrations-page-wrapper"
        style={{
          opacity:    visible ? 1 : 0,
          transform:  visible ? 'translateY(0)' : 'translateY(18px)',
          transition: 'opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        <div className="container-fluid px-4">

          {/* Search and Filters */}
          <section className="integrations-search-section">
            <div className="search-input-wrapper">
              <Search className="search-input-icon" size={18} />
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-field"
              />
            </div>
            <div className="search-actions">
              <button className="filter-platforms-btn"><Filter size={16} /> Filter Platforms</button>
              <div className="gateway-status-badge">
                <div className="gateway-pulse" />
                <span className="gateway-text">Gateway Optimal</span>
              </div>
            </div>
          </section>

          {/* Stats Summary */}
          {/* <section className="row g-4 mb-5">
            {statsSummary.map((stat, index) => (
              <div key={index} className="col-12 col-md-4">
                <ComingSoonCard>
                  <div className="integration-stat-card">
                    <div className={`integration-stat-icon ${stat.color}`}><stat.icon size={24} /></div>
                    <div className="integration-stat-info">
                      <p className="integration-stat-label">{stat.label}</p>
                      <p className="integration-stat-value">{stat.value}</p>
                    </div>
                  </div>
                </ComingSoonCard>
              </div>
            ))}
          </section> */}

          {/* Integration Cards Grid */}
          <section className="row g-4 mb-5">
            {filteredPlatforms.map((platform) => (
              <div key={platform.name} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                {platform.comingSoon
                  ? <ComingSoonCard>{renderCard(platform)}</ComingSoonCard>
                  : renderCard(platform)
                }
              </div>
            ))}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-3">
              <ComingSoonCard>
                <button className="add-integration-card" style={{ width: '100%' }}>
                  <div className="add-integration-icon"><Plus size={32} /></div>
                  <div className="add-integration-content">
                    <h3 className="add-integration-title">Request Custom Bridge</h3>
                    <p className="add-integration-text">
                      Need a platform not listed? Our team can build a custom API bridge for you.
                    </p>
                  </div>
                </button>
              </ComingSoonCard>
            </div>
          </section>

          {/* Footer Info */}
          <section className="security-info-section">
            <div className="security-info-card">
              <div className="security-info-left">
                <div className="security-info-icon"><AlertCircle size={32} /></div>
                <div className="security-info-content">
                  <h4 className="security-info-title">Security & API Protocols</h4>
                  <p className="security-info-text">
                    All platform integrations use 256-bit AES encryption and OAuth 2.0. Revexia
                    never stores raw credentials, only temporary access tokens with scoped permissions.
                  </p>
                </div>
              </div>
              <button className="security-docs-btn">View API Documentation</button>
            </div>
          </section>
        </div>
      </div>


      {/* ══════════════════════════════════════════════════════════════════════
          ManyChat Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showManyChatModal && (
        <div className="mc-modal-overlay" onClick={handleCloseModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon"><ManyChatIcon size={40} /></div>
                <div>
                  <h3 className="mc-modal-title">ManyChat Integration</h3>
                  <p className="mc-modal-subtitle">
                    {manyChatConnected && manyChatAccount
                      ? <>✅ Connected as <strong>{manyChatAccount.name}</strong></>
                      : 'Connect your ManyChat account via API key'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseModal}><X size={20} /></button>
            </div>

            {step === 'form' && (
              <form onSubmit={handleManyChatSubmit} className="mc-modal-body">
                <div className="mc-info-banner">
                  <Shield size={14} />
                  <span>Your API key is encrypted and stored securely. We never share your credentials.</span>
                </div>
                <div className="mc-field">
                  <label className="mc-label">
                    <Key size={13} className="mc-label-icon" />
                    ManyChat API Key <span className="mc-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="260049122536720:fead6e2998d556a8a8812b7a8..."
                    className="mc-input"
                    required
                    autoFocus
                  />
                  <p className="mc-field-hint">Find your API key in ManyChat → Settings → API</p>
                </div>
                {saveError && (
                  <div className="mc-alert mc-alert-error">
                    <AlertCircle size={15} /><span>{saveError}</span>
                  </div>
                )}
                <div className="mc-modal-footer">
                  <button type="button" className="mc-btn-cancel" onClick={handleCloseModal} disabled={isSaving}>Cancel</button>
                  <button type="submit" className="mc-btn-save" disabled={isSaving || !apiKey.trim()}>
                    {isSaving
                      ? <><Loader2 size={15} className="mc-spinner" /> Connecting...</>
                      : <><Key size={15} /> Save & Connect</>}
                  </button>
                </div>
              </form>
            )}

            {step === 'success' && accountData && (
              <div className="mc-modal-body">
                <div className="mc-success-banner">
                  <CheckCircle2 size={16} /><span>ManyChat account connected successfully!</span>
                </div>
                <div className="mc-account-card">
                  <div className="mc-account-avatar">
                    {accountData.avatar_link
                      ? <img src={accountData.avatar_link} alt={accountData.name} />
                      : <User size={28} />}
                  </div>
                  <div className="mc-account-info">
                    <div className="mc-account-name-row">
                      <h4 className="mc-account-name">{accountData.name}</h4>
                      {accountData.is_pro && <span className="mc-pro-badge"><Crown size={10} /> PRO</span>}
                    </div>
                    <p className="mc-account-category">{accountData.category}</p>
                    <p className="mc-account-username">@{accountData.username}</p>
                  </div>
                </div>
                <div className="mc-account-details">
                  {[
                    { icon: Globe,  label: 'Timezone',   value: accountData.timezone },
                    { icon: User,   label: 'Account ID', value: accountData.id },
                    { icon: Shield, label: 'Plan',       value: accountData.is_pro ? 'ManyChat Pro' : 'Free' },
                  ].map(({ icon: Icon, label, value }) => (
                    <div className="mc-detail-row" key={label}>
                      <span className="mc-detail-label"><Icon size={12} /> {label}</span>
                      <span className="mc-detail-value">{value}</span>
                    </div>
                  ))}
                </div>
                <div className="mc-modal-footer">
                  <button type="button" className="mc-btn-disconnect" onClick={handleDisconnect}>Disconnect</button>
                  <button type="button" className="mc-btn-save" onClick={handleCloseModal}>
                    <CheckCircle2 size={15} /> Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* ══════════════════════════════════════════════════════════════════════
          Facebook Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showFbModal && (
        <div className="mc-modal-overlay" onClick={handleCloseFbModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon">
                  <Facebook size={40} style={{ color: '#1877F2' }} />
                </div>
                <div>
                  <h3 className="mc-modal-title">Facebook Integration</h3>
                  <p className="mc-modal-subtitle">
                    {fbConnected
                      ? <>✅ {fbAccount?.page ? <>Connected as <strong>{fbAccount.page}</strong></> : 'Connected'}</>
                      : 'Connect your Facebook Page via OAuth'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseFbModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">
              {fbConnected && (
                <div className="mc-success-banner" style={{ marginBottom: '1rem' }}>
                  <CheckCircle2 size={16} />
                  <span>Facebook connected successfully{fbAccount?.page ? ` as ${fbAccount.page}` : ''}!</span>
                </div>
              )}
              <div className="mc-info-banner" style={{ marginBottom: '1.25rem' }}>
                <Shield size={14} />
                <span>We only request <strong>pages_messaging</strong> and <strong>pages_read_engagement</strong> permissions. No posting access.</span>
              </div>
              {fbConnecting && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '0.6rem', padding: '1rem',
                  background: 'rgba(24,119,242,0.08)',
                  border: '1px solid rgba(24,119,242,0.2)',
                  borderRadius: '8px', marginBottom: '1rem',
                  fontSize: '0.82rem', color: '#93c5fd',
                }}>
                  <Loader2 size={15} className="mc-spinner" />
                  <span>Waiting for authorization… Complete the popup then wait a moment.</span>
                </div>
              )}
              <div className="mc-field">
                <button
                  type="button"
                  disabled={fbConnecting || fbDisconnecting}
                  onClick={handleFbOAuth}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '0.5rem', padding: '0.75rem 1.25rem',
                    background: '#1877F2', color: '#fff',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '0.875rem', fontWeight: 700,
                    opacity: (fbConnecting || fbDisconnecting) ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {fbConnecting
                    ? <><Loader2 size={16} className="mc-spinner" /> Verifying connection...</>
                    : <><Facebook size={16} /> {fbConnected ? 'Reconnect Facebook Page' : 'Connect Facebook Page'}</>
                  }
                </button>
              </div>
              {fbError && !fbConnected && (
                <div className="mc-alert mc-alert-error" style={{ marginTop: '0.75rem' }}>
                  <AlertCircle size={15} /><span>{fbError}</span>
                </div>
              )}
              <div className="mc-modal-footer">
                {fbConnected && (
                  <button
                    type="button"
                    className="mc-btn-disconnect"
                    onClick={handleFbDisconnect}
                    disabled={fbDisconnecting || fbConnecting}
                  >
                    {fbDisconnecting
                      ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                      : 'Disconnect'}
                  </button>
                )}
                <button
                  type="button"
                  className="mc-btn-cancel"
                  onClick={handleCloseFbModal}
                  disabled={fbDisconnecting}
                >
                  {fbConnected ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ══════════════════════════════════════════════════════════════════════
          Instagram Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showIgModal && (
        <div className="mc-modal-overlay" onClick={handleCloseIgModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon"><InstagramIcon size={40} /></div>
                <div>
                  <h3 className="mc-modal-title">Instagram Integration</h3>
                  <p className="mc-modal-subtitle">
                    {igConnected
                      ? <>✅ {igAccount?.page ? <>Connected as <strong>{igAccount.page}</strong></> : 'Connected'}</>
                      : 'Connect your Instagram Business account via OAuth'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseIgModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">
              {igConnected && (
                <div className="mc-success-banner" style={{ marginBottom: '1rem' }}>
                  <CheckCircle2 size={16} />
                  <span>Instagram connected successfully{igAccount?.page ? ` as @${igAccount.page}` : ''}!</span>
                </div>
              )}
              <div className="mc-info-banner" style={{ marginBottom: '1.25rem' }}>
                <Shield size={14} />
                <span>
                  Requires an <strong>Instagram Business</strong> or <strong>Creator</strong> account
                  linked to a Facebook Page. We request{' '}
                  <strong>instagram_business_manage_messages</strong> permissions only.
                </span>
              </div>
              {igConnecting && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '0.6rem', padding: '1rem',
                  background: 'rgba(214,36,159,0.08)',
                  border: '1px solid rgba(214,36,159,0.2)',
                  borderRadius: '8px', marginBottom: '1rem',
                  fontSize: '0.82rem', color: '#f0abdc',
                }}>
                  <Loader2 size={15} className="mc-spinner" />
                  <span>Waiting for authorization… Complete the popup then wait a moment.</span>
                </div>
              )}
              <div className="mc-field">
                <button
                  type="button"
                  disabled={igConnecting || igDisconnecting}
                  onClick={handleIgOAuth}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '0.5rem', padding: '0.75rem 1.25rem',
                    background: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                    color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '0.875rem', fontWeight: 700,
                    opacity: (igConnecting || igDisconnecting) ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {igConnecting
                    ? <><Loader2 size={16} className="mc-spinner" /> Verifying connection...</>
                    : <><InstagramIcon size={16} /> {igConnected ? 'Reconnect Instagram' : 'Connect Instagram'}</>
                  }
                </button>
              </div>
              {igError && !igConnected && (
                <div className="mc-alert mc-alert-error" style={{ marginTop: '0.75rem' }}>
                  <AlertCircle size={15} /><span>{igError}</span>
                </div>
              )}
              <div className="mc-modal-footer">
                {igConnected && (
                  <button
                    type="button"
                    className="mc-btn-disconnect"
                    onClick={handleIgDisconnect}
                    disabled={igDisconnecting || igConnecting}
                  >
                    {igDisconnecting
                      ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                      : 'Disconnect'}
                  </button>
                )}
                <button
                  type="button"
                  className="mc-btn-cancel"
                  onClick={handleCloseIgModal}
                  disabled={igDisconnecting}
                >
                  {igConnected ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ══════════════════════════════════════════════════════════════════════
          Shopify Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showShopifyModal && (
        <div className="mc-modal-overlay" onClick={handleCloseShopifyModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon">
                  <ShoppingBag size={40} style={{ color: '#95BF47' }} />
                </div>
                <div>
                  <h3 className="mc-modal-title">Shopify Integration</h3>
                  <p className="mc-modal-subtitle">
                    {shopifyConnected && shopifyAccount
                      ? <>✅ Connected — <strong>{shopifyAccount.domain?.replace('https://', '').replace('/', '') || 'Store active'}</strong></>
                      : 'Install the tracking snippet on your Shopify store'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseShopifyModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">

              {/* ── Success state ── */}
              {shopifyStep === 'success' && (
                <>
                  <div className="mc-success-banner" style={{ marginBottom: '1rem' }}>
                    <CheckCircle2 size={16} />
                    <span>Shopify tracker is active and collecting events!</span>
                  </div>

                  {/* Store details */}
                  {shopifyAccount && (
                    <div className="mc-account-card" style={{ marginBottom: '1rem' }}>
                      <div className="mc-account-avatar" style={{
                        background: 'rgba(149,191,71,0.12)', border: '1px solid rgba(149,191,71,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 48, height: 48, borderRadius: 10,
                      }}>
                        <ShoppingBag size={22} style={{ color: '#95BF47' }} />
                      </div>
                      <div className="mc-account-info">
                        <div className="mc-account-name-row">
                          <h4 className="mc-account-name">{shopifyAccount.domain?.replace('https://', '').replace('/', '') || 'Shopify Store'}</h4>
                          <span style={{ fontSize: '0.6rem', fontWeight: 800, background: 'rgba(149,191,71,0.15)', color: '#95BF47', border: '1px solid rgba(149,191,71,0.3)', borderRadius: 999, padding: '0.15rem 0.5rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Active</span>
                        </div>
                        <p className="mc-account-username">Site ID: {shopifyAccount.siteId}</p>
                      </div>
                    </div>
                  )}

                  <div className="mc-account-details">
                    {[
                      { icon: Activity,     label: 'Events Tracked', value: String(shopifyEvents) },
                      { icon: RefreshCw,    label: 'Last Synced',    value: shopifyLastSync },
                    ].map(({ icon: Icon, label, value }) => (
                      <div className="mc-detail-row" key={label}>
                        <span className="mc-detail-label"><Icon size={12} /> {label}</span>
                        <span className="mc-detail-value">{value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Show snippet for reference */}
                  {(shopifySnippet || shopifySiteId) && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Installed Snippet</p>
                      <div style={{
                        background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 8, padding: '0.75rem', fontFamily: 'monospace',
                        fontSize: '0.65rem', color: '#6EE7B7', lineHeight: 1.7,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 120, overflowY: 'auto',
                      }}>
                        {shopifySnippet || buildSnippet(shopifySiteId)}
                      </div>
                    </div>
                  )}

                  <div className="mc-modal-footer" style={{ marginTop: '1.25rem' }}>
                    <button
                      type="button"
                      className="mc-btn-disconnect"
                      onClick={handleShopifyDisconnect}
                      disabled={shopifyDisconnecting}
                    >
                      {shopifyDisconnecting
                        ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                        : 'Disconnect'}
                    </button>
                    <button
                      type="button"
                      className="mc-btn-cancel"
                      onClick={handleCloseShopifyModal}
                      disabled={shopifyDisconnecting}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}

              {/* ── Install state ── */}
              {shopifyStep === 'install' && (
                <>
                  <div className="mc-info-banner" style={{ marginBottom: '1.25rem' }}>
                    <Shield size={14} />
                    <span>Paste this snippet into your Shopify theme's <strong>theme.liquid</strong> file, just before the closing <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0 4px', borderRadius: 3 }}>&lt;/body&gt;</code> tag.</span>
                  </div>

                  {/* Step 1: Generate ID */}
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Step 1 — Your Tracking Snippet
                    </p>

                    {shopifyGenerating ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1.5rem', background: 'rgba(149,191,71,0.05)', border: '1px solid rgba(149,191,71,0.15)', borderRadius: 8 }}>
                        <Loader2 size={15} className="mc-spinner" style={{ color: '#95BF47' }} />
                        <span style={{ fontSize: '0.77rem', color: '#95BF47' }}>Generating your unique Site ID…</span>
                      </div>
                    ) : shopifySiteId ? (
                      <>
                        <div style={{
                          background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(149,191,71,0.2)',
                          borderRadius: 8, padding: '0.85rem',
                          fontFamily: 'monospace', fontSize: '0.64rem', color: '#6EE7B7',
                          lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                          maxHeight: 160, overflowY: 'auto', marginBottom: '0.6rem',
                        }}>
                          {shopifySnippet || buildSnippet(shopifySiteId)}
                        </div>
                        <button
                          onClick={handleShopifyCopySnippet}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '0.45rem', padding: '0.6rem 1rem',
                            background: shopifyCopied ? 'rgba(16,185,129,0.15)' : 'rgba(149,191,71,0.1)',
                            border: `1px solid ${shopifyCopied ? 'rgba(16,185,129,0.35)' : 'rgba(149,191,71,0.3)'}`,
                            borderRadius: 8, color: shopifyCopied ? '#6EE7B7' : '#95BF47',
                            fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                          }}
                        >
                          {shopifyCopied
                            ? <><CheckCircle2 size={14} /> Copied to clipboard!</>
                            : <><Key size={14} /> Copy Snippet</>}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleShopifyGenerateId}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: '0.45rem', padding: '0.65rem 1rem',
                          background: 'rgba(149,191,71,0.1)', border: '1px solid rgba(149,191,71,0.3)',
                          borderRadius: 8, color: '#95BF47', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        <Zap size={14} /> Generate Site ID & Snippet
                      </button>
                    )}
                  </div>

                  {/* Step 2: Instructions */}
                  {shopifySiteId && (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Step 2 — Install in Shopify
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                        {[
                          'Go to your Shopify Admin → Online Store → Themes',
                          'Click Actions → Edit Code on your active theme',
                          'Open Layout → theme.liquid',
                          'Paste the snippet just before </body> and click Save',
                        ].map((step, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                            <span style={{
                              width: 18, height: 18, borderRadius: '50%', background: 'rgba(149,191,71,0.15)',
                              border: '1px solid rgba(149,191,71,0.3)', color: '#95BF47',
                              fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center',
                              justifyContent: 'center', flexShrink: 0, marginTop: 1,
                            }}>{i + 1}</span>
                            <span style={{ fontSize: '0.74rem', color: '#9CA3AF', lineHeight: 1.5 }}>{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {shopifyError && (
                    <div className="mc-alert mc-alert-error" style={{ marginBottom: '0.75rem' }}>
                      <AlertCircle size={15} /><span>{shopifyError}</span>
                    </div>
                  )}

                  <div className="mc-modal-footer">
                    <button type="button" className="mc-btn-cancel" onClick={handleCloseShopifyModal}>Cancel</button>
                    {shopifySiteId && (
                      <button
                        type="button"
                        className="mc-btn-save"
                        onClick={handleShopifyVerify}
                        disabled={shopifyVerifying}
                      >
                        {shopifyVerifying
                          ? <><Loader2 size={15} className="mc-spinner" /> Verifying…</>
                          : <><CheckCircle2 size={15} /> I've Installed It</>}
                      </button>
                    )}
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Integrations;